import { useState } from 'react';
import { VStack, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SocialAuthButton } from '@/components/ui/SocialAuthButton';
import { socialStart, SOCIAL_PROVIDER_KEY, type SocialProvider } from '@/features/auth/emailAuth';

interface OAuthButtonsProps {
  onOAuthStart?: (provider: string) => void;
  disabled?: boolean;
}

/**
 * Provider-branded sign-in buttons. Clicking one starts a Zitadel IdP intent on
 * the backend (POST /auth/social/start) and full-page-redirects the browser
 * straight to the provider's consent screen — no Zitadel hosted-login chooser.
 * The provider redirects back to `/auth/idp-callback`, where {@link IdpCallbackPage}
 * finishes sign-in. The chosen provider is stashed in sessionStorage so the
 * callback (a fresh page load) knows which provider to finish.
 */
export function OAuthButtons({ onOAuthStart, disabled = false }: OAuthButtonsProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const handleOAuthClick = async (provider: SocialProvider) => {
    if (busy) return;
    onOAuthStart?.(provider);
    setBusy(true);
    try {
      const redirectUri = `${window.location.origin}/auth/idp-callback`;
      const { authUrl } = await socialStart(provider, redirectUri);
      // Carry the provider across the full-page redirect (same tab/origin, so
      // sessionStorage survives) — the callback page needs it for social/finish.
      sessionStorage.setItem(SOCIAL_PROVIDER_KEY, provider);
      window.location.assign(authUrl);
      // Navigation is underway; keep the buttons disabled (no finally reset).
    } catch (err) {
      console.error('[OAuthButtons] social start failed', err);
      setBusy(false);
      toast({
        status: 'error',
        description: err instanceof Error ? err.message : t('auth.socialLoginFailed'),
        duration: 8000,
        isClosable: true,
      });
    }
  };

  const isDisabled = disabled || busy;

  return (
    <VStack spacing={3} w="100%">
      <SocialAuthButton
        provider="google"
        onPress={() => handleOAuthClick('google')}
        disabled={isDisabled}
        testID="oauth-google"
      />
      <SocialAuthButton
        provider="apple"
        onPress={() => handleOAuthClick('apple')}
        disabled={isDisabled}
        testID="oauth-apple"
      />
      <SocialAuthButton
        provider="facebook"
        onPress={() => handleOAuthClick('facebook')}
        disabled={isDisabled}
        testID="oauth-facebook"
      />
    </VStack>
  );
}

export default OAuthButtons;
